<template>
  <UInputDate
    v-model="calendarDateValue"
    :locale="$i18n.locale"
    :min-value="minCalendarDate"
    :max-value="maxCalendarDate"
    :disabled="disabled"
    :readonly="readonly"
    :size="uiSize"
    :color="uiColor"
    :class="$attrs.class"
    :ui="ui"
  >
    <template v-if="$slots.leading" #leading>
      <slot name="leading" />
    </template>

    <template v-if="$slots.trailing || showClear || showPicker" #trailing>
      <div class="flex items-center gap-1">
        <slot name="trailing" />

        <UButton
          v-if="showClear"
          color="error"
          variant="link"
          size="xs"
          square
          icon="i-lucide-x"
          :aria-label="$t('general.clear')"
          @click.stop="clearValue"
        />

        <UPopover
          v-if="showPicker"
          v-model:open="pickerOpen"
          :content="{ side: 'bottom', align: 'end', sideOffset: 8, collisionPadding: 12 }"
        >
          <UButton
            color="neutral"
            variant="link"
            size="sm"
            square
            icon="i-lucide-calendar"
            :aria-label="$t('general.date')"
            tabindex="-1"
          />

          <template #content>
            <ClientOnly>
              <UCalendar
                v-model="calendarDateValue"
                :locale="$i18n.locale"
                :week-starts-on="weekStartsOn"
                :min-value="minCalendarDate"
                :max-value="maxCalendarDate"
                class="p-2"
                @update:model-value="pickerOpen = false"
              />
            </ClientOnly>
          </template>
        </UPopover>
      </div>
    </template>
  </UInputDate>
</template>

<script setup lang="ts">
import { CalendarDate, type DateValue } from '@internationalized/date'
import { UButton, UCalendar, UPopover } from '#components'
import { augmenters } from '@/composables/useFieldAugmenters'
import { useCalendarFirstDayOfWeek } from '@/composables/useCalendarFirstDayOfWeek'

const { $auth } = useNuxtApp()

interface Props {
  modelValue?: string | number | Record<string, any> | Date | null
  range?: boolean
  picker?: boolean
  minDate?: Date | null
  maxDate?: Date | null
  augmenter?: Record<string, any> | boolean
  visibility?: string
  timezone?: string
  mode?: string
  positionFixed?: boolean
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  range: false,
  picker: true,
  minDate: null,
  maxDate: null,
  augmenter: () => augmenters.YMD,
  visibility: 'click',
  timezone: undefined,
  mode: 'date',
  positionFixed: false,
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'px-2 h-9',
  largeSizeClass: 'px-4 text-lg h-12',
  smallSizeClass: 'px-2 text-sm h-8',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

defineOptions({ inheritAttrs: false })

const pickerOpen = ref(false)

const showPicker = computed(() => props.picker && !props.readonly && !props.disabled)
const showClear = computed(() => props.clear && calendarDateValue.value != null && !props.readonly && !props.disabled)

// Composable normalises the 1-7 StartDayOfWeek enum to UCalendar's 0-6 scheme.
const weekStartsOn = useCalendarFirstDayOfWeek(() => ($auth as any)?.info?.location?.startDayOfWeek)

const uiSize = computed(() => {
  if (props.size === 'sm') return 'sm'
  if (props.size === 'lg') return 'lg'
  return 'md'
})

const uiColor = computed(() => {
  if (props.status === false || props.status === 'error') return 'error' as any
  return 'neutral' as any
})

const ui = computed(() => ({
  base: 'w-full',
}))

function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

function calendarDateToDate(cd: DateValue): Date {
  return new Date(cd.year, cd.month - 1, cd.day)
}

const minCalendarDate = computed(() => {
  if (!props.minDate) return undefined
  return dateToCalendarDate(props.minDate)
})

const maxCalendarDate = computed(() => {
  if (!props.maxDate) return undefined
  return dateToCalendarDate(props.maxDate)
})

const calendarDateValue = computed({
  get(): DateValue | undefined {
    if (props.modelValue == null || props.modelValue === '') return undefined

    const aug = props.augmenter as any
    if (aug && aug.input) {
      const date = aug.input(props.modelValue)
      if (date instanceof Date) {
        return dateToCalendarDate(date)
      }
    }

    if (props.modelValue instanceof Date) {
      return dateToCalendarDate(props.modelValue)
    }

    return undefined
  },
  set(value: DateValue | undefined) {
    if (!value) {
      emit('update:modelValue', null)
      return
    }

    const date = calendarDateToDate(value)
    const aug = props.augmenter as any
    if (aug && aug.output) {
      emit('update:modelValue', aug.output(date))
    } else {
      emit('update:modelValue', date)
    }
  },
})

function clearValue() {
  calendarDateValue.value = undefined
}
</script>
